import { useState, useEffect, useCallback, useRef } from 'react';
import io, { Socket } from 'socket.io-client';

// ============================================================================
// Core Types
// ============================================================================

export interface ApplicationInfo {
  id: string;
  key: string;
  name: string;
  description?: string;
  version?: string;
  status: 'published' | 'draft' | 'rebuild_required' | 'error';
  openApiSpec?: any;
  createdAt: Date;
  updatedAt: Date;
}

export interface ApplicationFunction {
  id: string;
  applicationId: string;
  applicationKey: string;
  operationId: string;
  name: string;
  path: string;
  method: string;
  description?: string;
  implementationType?: 'sql' | 'integration' | 'custom' | 'none';
  hasImplementation: boolean;
}

export interface Environment {
  id: string;
  name: string;
  key: string;
  type: 'development' | 'staging' | 'production';
  url?: string;
  status: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
  applications: string[]; // Application IDs deployed
  lastHealthCheck?: Date;
}

// ============================================================================
// Client Types - Agent and UI Clients
// ============================================================================

export type ClientType = 'agent' | 'ui' | 'mcp' | 'api' | 'webhook';

export interface Client {
  id: string;
  name: string;
  type: ClientType;
  applicationId: string;
  applicationKey: string;
  description?: string;
  metadata?: Record<string, any>;
}

export interface AgentClient extends Client {
  type: 'agent' | 'mcp';
  agentId?: string;
  agentKey?: string;
  model?: string;
  conversationCount: number;
  lastConversation?: Date;
  capabilities?: string[];
}

export interface UIClient extends Client {
  type: 'ui';
  platform: 'web' | 'mobile' | 'desktop' | 'embedded';
  version?: string;
  userCount: number;
  sessionCount: number;
  lastSession?: Date;
}

export interface APIClient extends Client {
  type: 'api' | 'webhook';
  apiKeyId?: string;
  requestCount: number;
  lastRequest?: Date;
}

// ============================================================================
// Analytics Types - Full Stack Observability
// ============================================================================

export interface ClientAnalytics {
  clientId: string;
  clientType: ClientType;
  period: 'hour' | 'day' | 'week' | 'month';
  
  // Request Metrics
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  errorRate: number;
  
  // Performance Metrics
  avgResponseTime: number;
  p50ResponseTime: number;
  p95ResponseTime: number;
  p99ResponseTime: number;
  
  // Error Breakdown
  errors: {
    type: string;
    count: number;
    lastOccurred: Date;
    sampleMessage?: string;
  }[];
  
  // Crash/Fatal Errors (for UI clients)
  crashes?: {
    count: number;
    lastCrash?: Date;
    affectedSessions: number;
  };
  
  // Top Functions Called
  topFunctions: {
    functionId: string;
    name: string;
    callCount: number;
    avgDuration: number;
    errorCount: number;
  }[];
}

export interface FunctionCallChain {
  id: string;
  traceId: string;
  clientId: string;
  clientType: ClientType;
  timestamp: Date;
  duration: number;
  success: boolean;
  
  // The chain of calls
  spans: {
    spanId: string;
    parentSpanId?: string;
    name: string;
    service: string;
    operation: string;
    duration: number;
    status: 'ok' | 'error';
    attributes?: Record<string, any>;
  }[];
}

// ============================================================================
// Statistics Types
// ============================================================================

export interface ApplicationObservatoryStatistics {
  // Application counts
  totalApplications: number;
  publishedApplications: number;
  draftApplications: number;
  rebuildRequired: number;
  
  // Function counts
  totalFunctions: number;
  implementedFunctions: number;
  unimplementedFunctions: number;
  
  // Environment counts
  totalEnvironments: number;
  healthyEnvironments: number;
  degradedEnvironments: number;
  
  // Client counts
  totalClients: number;
  agentClients: number;
  uiClients: number;
  apiClients: number;
  
  // Activity metrics
  totalApiCalls: number;
  apiCallsToday: number;
  apiCallsThisHour: number;
  averageResponseTime: number;
  errorRate: number;
  
  // Top performers
  mostActiveApplication?: {
    id: string;
    name: string;
    callCount: number;
  };
  slowestFunction?: {
    id: string;
    name: string;
    avgDuration: number;
  };
}

export interface ApplicationObservatorySnapshot {
  activeSessions: {
    clientId: string;
    clientType: ClientType;
    applicationId: string;
    startedAt: Date;
  }[];
  activeRequests: number;
  functionMetrics: Record<string, { count: number; totalMs: number }>;
  connectedClients: number;
  recentErrors: {
    timestamp: Date;
    clientId: string;
    functionName: string;
    error: string;
  }[];
}

export interface ApplicationObservatoryEvent {
  type: 'request:started' | 'request:completed' | 'request:failed' |
        'function:called' | 'error:occurred' | 'client:connected' |
        'client:disconnected' | 'application:deployed' | 'application:updated';
  data: any;
  timestamp: string;
}

// ============================================================================
// Hook
// ============================================================================

export function useApplicationObservatory(apiUrl: string) {
  const [connected, setConnected] = useState(false);
  const [snapshot, setSnapshot] = useState<ApplicationObservatorySnapshot | null>(null);
  const [events, setEvents] = useState<ApplicationObservatoryEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  const socketRef = useRef<Socket | null>(null);
  const maxEvents = 100;

  useEffect(() => {
    const connectWithAuth = async () => {
      let authToken = '';
      try {
        if ((window as any).authProvider?.getIdToken) {
          authToken = await (window as any).authProvider.getIdToken();
          console.log('🔐 [App Observatory] Got auth token for WebSocket');
        }
      } catch (error) {
        console.warn('⚠️ [App Observatory] Could not get auth token:', error);
      }

      const socket = io(`${apiUrl}/application-observatory`, {
        transports: ['websocket'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
        auth: {
          token: authToken,
        },
        extraHeaders: authToken ? {
          'Authorization': `Bearer ${authToken}`,
        } : {},
      });

      socketRef.current = socket;

      socket.on('connect', () => {
        console.log('✅ Connected to Application Observatory');
        setConnected(true);
        setError(null);
      });

      socket.on('disconnect', () => {
        console.log('❌ Disconnected from Application Observatory');
        setConnected(false);
      });

      socket.on('connect_error', (err) => {
        console.error('Application Observatory connection error:', err);
        setError(`Connection error: ${err.message}`);
      });

      // Initial snapshot
      socket.on('observatory:snapshot', (data: ApplicationObservatorySnapshot) => {
        console.log('📊 Received application observatory snapshot', data);
        setSnapshot(data);
      });

      // Real-time events
      const eventTypes = [
        'request:started', 'request:completed', 'request:failed',
        'function:called', 'error:occurred', 'client:connected',
        'client:disconnected', 'application:deployed', 'application:updated'
      ];

      eventTypes.forEach(eventType => {
        socket.on(eventType, (data) => {
          addEvent(eventType as ApplicationObservatoryEvent['type'], data);
        });
      });

      return () => {
        socket.disconnect();
      };
    };

    connectWithAuth();
  }, [apiUrl]);

  const addEvent = useCallback((type: ApplicationObservatoryEvent['type'], data: any) => {
    const event: ApplicationObservatoryEvent = {
      type,
      data,
      timestamp: data.timestamp || new Date().toISOString(),
    };

    setEvents((prev) => {
      const newEvents = [event, ...prev];
      return newEvents.slice(0, maxEvents);
    });
  }, []);

  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  return {
    connected,
    snapshot,
    events,
    error,
    clearEvents,
  };
}

export default useApplicationObservatory;

